// Google reCAPTCHA v3 (score-based, invisible) token minting for the legacy
// (non-Mantine) signup form. Mirrors the @hasura/auth-ui reCAPTCHA helper but
// lives locally for the custom SignupForm. It uses module-local window access
// (NOT a global `Window` augmentation) so it never collides with the
// @hasura/auth-ui bundle's own `window.grecaptcha` declaration.
//
// The token is minted on submit via grecaptcha.execute(siteKey, {action}) and
// sent as `google_recaptcha_token`; the action MUST match the auth service's
// registerRecaptchaAction ("register").

const RECAPTCHA_V3_SCRIPT_SRC = 'https://www.google.com/recaptcha/api.js';

// A blocked network (corporate proxy / adblocker) sometimes black-holes the
// request so it hangs instead of firing onerror. Without a timeout the caller
// would wait forever; time out so a blocked domain surfaces a retryable error.
const SCRIPT_LOAD_TIMEOUT_MS = 15000;

type RecaptchaV3Api = {
  ready: (cb: () => void) => void;
  execute: (siteKey: string, opts: { action: string }) => Promise<string>;
};

const getGrecaptcha = (): RecaptchaV3Api | undefined =>
  (window as unknown as { grecaptcha?: RecaptchaV3Api }).grecaptcha;

let scriptPromise: Promise<RecaptchaV3Api> | null = null;

export const loadRecaptchaV3 = (siteKey: string): Promise<RecaptchaV3Api> => {
  const existing = getGrecaptcha();
  if (existing?.execute) return Promise.resolve(existing);
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise<RecaptchaV3Api>((resolve, reject) => {
    let settled = false;
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      scriptPromise = null; // allow a later attempt to retry the load
      reject(new Error('reCAPTCHA script load timed out'));
    }, SCRIPT_LOAD_TIMEOUT_MS);

    const script = document.createElement('script');
    script.src = `${RECAPTCHA_V3_SCRIPT_SRC}?render=${encodeURIComponent(
      siteKey
    )}`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (settled) return;
      const ts = getGrecaptcha();
      if (!ts?.ready) {
        settled = true;
        clearTimeout(timer);
        scriptPromise = null;
        reject(new Error('reCAPTCHA loaded but grecaptcha.ready is missing'));
        return;
      }
      // Resolve only once reCAPTCHA is fully initialised — grecaptcha.execute is
      // wired up a beat after onload, so ready() is the correct gate. (Resolving
      // at onload caused the first execute to fail with "execute missing".)
      ts.ready(() => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve(ts);
      });
    };
    script.onerror = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      scriptPromise = null;
      reject(new Error('Failed to load the reCAPTCHA script'));
    };
    document.head.appendChild(script);
  });

  return scriptPromise;
};

/**
 * Mint a fresh, single-use reCAPTCHA v3 token for the given action. Rejects if
 * the script fails to load or times out (e.g. the domain is blocked) so the
 * caller can surface a retryable error.
 */
export const executeRecaptchaV3 = async (
  siteKey: string,
  action: string
): Promise<string> => {
  const grecaptcha = await loadRecaptchaV3(siteKey);
  return new Promise<string>((resolve, reject) => {
    grecaptcha.ready(() => {
      grecaptcha.execute(siteKey, { action }).then(resolve).catch(reject);
    });
  });
};
